import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { RootState } from '@/reducers/reducers';
import {
    selectDestinationInfoByDestinationStepConfigId,
    selectAppStoreIntegration,
    selectStabilityIntegration,
} from '@/selectors/selectors';
import {
    selectAvailableDestinations,
    selectAvailableVersions,
    createSummaryRequestId,
    selectSummaryViewByRequestId,
} from '@/selectors/summaryViewSelectors';
import { Destination } from '@/types/destinations';
import {
    PageViewType,
    TabularLayoutType,
    SummaryViewTableRow,
    OrgSummaryPreferences,
    OrgSummaryPreferencesSnapshot,
    FetchSummaryViewParams,
    UseOrgSummaryParams,
    UseSummaryPreferencesProps,
    FilterOption,
    SUPPORTED_STATISTIC_TYPES,
} from '@/types/summaryView';
import { useIsLoading, useError } from '@/utils/hooks/helpers';
import { FetchableEntityType } from '@/entityType';
import { tryFetchEntity } from '@/actions/entities';
import {
    getAppSummaryPreferences,
    setAppSummaryPreferences,
    getOrgSummaryPreferences,
    setOrgSummaryPreferences,
} from '@/helpers/localStorageHelper';
import {
    getAvailableMetricKeys,
    getAvailableMetricOptions,
    createFilterContexts,
    createDestinationContext,
    buildFetchRequest,
    transformToTableRows,
    filterToAvailable,
    mergePreferences,
    normalizePageView,
    normalizeTabularLayout,
    parseListParam,
    buildUrlSearchParams,
    createStandardViewColumns,
    createCompactViewConfig,
} from '@/helpers/summaryViewHelpers';
import { compareVersions } from '@/helpers';
import { setOrgSummaryAppPreferences, setOrgSummaryOrgPreferences } from '@/reducers/summaryViewPreferences';
import { createSummaryViewMetrics } from '@/components/orgSummary/overviewTableMetrics';

/**
 * Hook for managing preferences and URL synchronization
 */
const useSummaryPreferences = (props: UseSummaryPreferencesProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const getStoredPreferences = React.useCallback(() => {
        return props.appId ? getAppSummaryPreferences(props.appId) : getOrgSummaryPreferences();
    }, [props.appId]);

    const setStoredPreferences = React.useCallback(
        (preferences: {
            pageView: PageViewType;
            tabularLayout: TabularLayoutType;
            selectedMetrics: string[];
            selectedVersions: string[];
            selectedDestinationIds: string[];
        }) => {
            if (props.appId) {
                return setAppSummaryPreferences(props.appId, preferences);
            }
            return setOrgSummaryPreferences(preferences);
        },
        [props.appId],
    );

    const [pageView, setPageViewState] = React.useState<PageViewType>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const stored = getStoredPreferences();
        const pageViewParam = urlParams.get('pageView');
        if (pageViewParam) {
            return normalizePageView(pageViewParam);
        }
        return normalizePageView(stored.pageView);
    });

    const [tabularLayout, setTabularLayoutState] = React.useState<TabularLayoutType>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const stored = getStoredPreferences();
        const tabularLayoutParam = urlParams.get('tabularLayout');
        if (tabularLayoutParam) {
            return normalizeTabularLayout(tabularLayoutParam);
        }
        return normalizeTabularLayout(stored.tabularLayout);
    });

    const [selectedMetricKeys, setSelectedMetricKeysState] = React.useState<string[]>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const stored = getStoredPreferences();
        const fromUrl = parseListParam(urlParams.get('metrics'));
        const fromStorage = stored.selectedMetrics;
        return filterToAvailable(fromUrl ?? fromStorage, props.availableMetrics);
    });

    const [selectedVersions, setSelectedVersionsState] = React.useState<string[]>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const stored = getStoredPreferences();
        const fromUrl = parseListParam(urlParams.get('versions'));
        const fromStorage = stored.selectedVersions;
        return filterToAvailable(fromUrl ?? fromStorage, props.availableVersions);
    });

    const [selectedDestinationIds, setSelectedDestinationIdsState] = React.useState<string[]>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const stored = getStoredPreferences();
        const fromUrl = parseListParam(urlParams.get('destinations'));
        const fromStorage = stored.selectedDestinationIds;
        return filterToAvailable(fromUrl ?? fromStorage, props.availableDestinationIds);
    });

    const hasInitializedRef = React.useRef(false);
    const hasInitialUrlSyncRef = React.useRef(false);

    React.useEffect(() => {
        if (hasInitialUrlSyncRef.current) return;
        if (props.availableVersions.length === 0 && props.availableDestinationIds.length === 0) return;
        hasInitialUrlSyncRef.current = true;

        const urlParams = new URLSearchParams(location.search);
        const hasUrlParams =
            urlParams.has('pageView') ||
            urlParams.has('tabularLayout') ||
            urlParams.has('metrics') ||
            urlParams.has('versions') ||
            urlParams.has('destinations');

        if (!hasUrlParams) {
            const currentPrefs = {
                pageView,
                tabularLayout,
                selectedMetrics: selectedMetricKeys,
                selectedVersions,
                selectedDestinationIds,
            };
            const params = buildUrlSearchParams(currentPrefs, location);
            if (params.toString()) {
                history.replace({ ...location, search: params.toString() });
            }
        }
    }, [props.availableVersions, props.availableDestinationIds]);

    const initializeWithDefaults = React.useCallback(
        (defaults: { versions: string[]; destinationIds: string[] }) => {
            if (hasInitializedRef.current) return;
            hasInitializedRef.current = true;

            const urlParams = new URLSearchParams(window.location.search);
            const stored = getStoredPreferences();

            const versionsFromUrl = parseListParam(urlParams.get('versions'));
            const versionsFromStorage = stored.selectedVersions;
            const destinationsFromUrl = parseListParam(urlParams.get('destinations'));
            const destinationsFromStorage = stored.selectedDestinationIds;

            const resolvedVersions = filterToAvailable(versionsFromUrl ?? versionsFromStorage, defaults.versions);
            const resolvedDestinations = filterToAvailable(
                destinationsFromUrl ?? destinationsFromStorage,
                defaults.destinationIds,
            );

            const finalVersions = resolvedVersions.length > 0 ? resolvedVersions : defaults.versions;
            const finalDestinations = resolvedDestinations.length > 0 ? resolvedDestinations : defaults.destinationIds;

            const needsVersionSync = selectedVersions.length === 0 && finalVersions.length > 0;
            const needsDestinationSync = selectedDestinationIds.length === 0 && finalDestinations.length > 0;

            if (needsVersionSync) {
                setSelectedVersionsState(finalVersions);
            }
            if (needsDestinationSync) {
                setSelectedDestinationIdsState(finalDestinations);
            }

            if (needsVersionSync || needsDestinationSync) {
                const updatedPrefs = {
                    pageView,
                    tabularLayout,
                    selectedMetrics: selectedMetricKeys,
                    selectedVersions: needsVersionSync ? finalVersions : selectedVersions,
                    selectedDestinationIds: needsDestinationSync ? finalDestinations : selectedDestinationIds,
                };
                setStoredPreferences(updatedPrefs);
                const params = buildUrlSearchParams(updatedPrefs, location);
                history.replace({ ...location, search: params.toString() });
            }
        },
        [
            selectedVersions,
            selectedDestinationIds,
            pageView,
            tabularLayout,
            selectedMetricKeys,
            getStoredPreferences,
            setStoredPreferences,
            history,
            location,
        ],
    );

    const persistToStore = React.useCallback(
        (prefs: OrgSummaryPreferences) => {
            if (props.appId) {
                dispatch(setOrgSummaryAppPreferences(props.appId, prefs));
            } else {
                dispatch(setOrgSummaryOrgPreferences(props.orgId, prefs));
            }
        },
        [dispatch, props.appId, props.orgId],
    );

    const updatePreferences = React.useCallback(
        (changes: {
            pageView?: PageViewType;
            tabularLayout?: TabularLayoutType;
            metrics?: string[];
            versions?: string[];
            destinations?: string[];
        }) => {
            let nextPageView = pageView;
            let nextTabularLayout = tabularLayout;

            if (changes.pageView !== undefined) {
                nextPageView = changes.pageView;
                setPageViewState(nextPageView);
            }
            if (changes.tabularLayout !== undefined) {
                nextTabularLayout = changes.tabularLayout;
                setTabularLayoutState(nextTabularLayout);
            }

            const current = {
                pageView: nextPageView,
                tabularLayout: nextTabularLayout,
                selectedMetrics: selectedMetricKeys,
                selectedVersions,
                selectedDestinationIds,
            };

            const available = {
                metrics: props.availableMetrics,
                versions: props.availableVersions,
                destinationIds: props.availableDestinationIds,
            };

            const updatedPrefs = mergePreferences(changes, current, available);

            if (changes.metrics !== undefined) setSelectedMetricKeysState(updatedPrefs.selectedMetrics);
            if (changes.versions !== undefined) setSelectedVersionsState(updatedPrefs.selectedVersions);
            if (changes.destinations !== undefined) setSelectedDestinationIdsState(updatedPrefs.selectedDestinationIds);

            setStoredPreferences(updatedPrefs);
            persistToStore(updatedPrefs);

            const params = buildUrlSearchParams(updatedPrefs, location);
            history.replace({ ...location, search: params.toString() });

            return updatedPrefs;
        },
        [
            pageView,
            tabularLayout,
            selectedMetricKeys,
            selectedVersions,
            selectedDestinationIds,
            props.availableMetrics,
            props.availableVersions,
            props.availableDestinationIds,
            history,
            location,
            setStoredPreferences,
            persistToStore,
        ],
    );

    return {
        pageView,
        tabularLayout,
        selectedMetricKeys,
        selectedVersions,
        selectedDestinationIds,
        updatePreferences,
        getStoredPreferences,
        initializeWithDefaults,
    };
};

export interface SummaryViewContextValue {
    preferences: OrgSummaryPreferencesSnapshot;
    updatePreferences: ReturnType<typeof useSummaryPreferences>['updatePreferences'];
    initializeWithDefaults: ReturnType<typeof useSummaryPreferences>['initializeWithDefaults'];
    getStoredPreferences: ReturnType<typeof useSummaryPreferences>['getStoredPreferences'];
    availableDestinations: Destination[];
    availableMetrics: FilterOption[];
    selectableVersions: string[];
    appStoreIntegration: ReturnType<typeof selectAppStoreIntegration>;
    stabilityIntegration: ReturnType<typeof selectStabilityIntegration>;
    isFilterApplying: boolean;
    setIsFilterApplying: React.Dispatch<React.SetStateAction<boolean>>;
    shouldDisplayFetchFailed: boolean;
    setShouldDisplayFetchFailed: React.Dispatch<React.SetStateAction<boolean>>;
    fetchSummaryView: (params: FetchSummaryViewParams) => void;
    requestId: string;
    summaryViewResponse: ReturnType<typeof selectSummaryViewByRequestId>;
    isLoadingSummaryView: boolean;
    availableVersions: string[];
    availableDestinationIds: string[];
    selectedDestinationIds: string[];
    handleFilterApply: (metrics: string[], versions: string[]) => void;
    handleDestinationChange: (ids: string[]) => void;
    handlePageViewChange: (pageView: PageViewType) => void;
    handleTabularLayoutChange: (tabularLayout: TabularLayoutType) => void;
    isLoadingOrgDestinations: boolean;
    tableData: {
        standardColumns: ReturnType<typeof createStandardViewColumns>;
        compactConfig: ReturnType<typeof createCompactViewConfig>;
        transformedRows: SummaryViewTableRow[];
        filteredRows: SummaryViewTableRow[];
        metrics: ReturnType<typeof createSummaryViewMetrics>;
    };
}

/**
 * Main hook for summary view functionality
 */
export const useSummaryView = ({
    orgId,
    appId,
    availableDestinations: propDestinations,
}: UseOrgSummaryParams): SummaryViewContextValue => {
    const dispatch = useDispatch();

    const selectorDestinations = useSelector((state: RootState) => selectAvailableDestinations(state, orgId, appId));
    const selectorVersions = useSelector((state: RootState) => selectAvailableVersions(state, orgId, appId));
    const isLoadingOrgDestinations = useIsLoading(FetchableEntityType.OrgDestinationsAndVersions, orgId);

    const storeDestinations = useSelector((state: RootState) =>
        appId ? Object.values(selectDestinationInfoByDestinationStepConfigId(state, appId)) : [],
    );

    const resolvedDestinations = React.useMemo(() => {
        if (propDestinations && propDestinations.length > 0) return propDestinations;
        if (selectorDestinations.length > 0) return selectorDestinations;
        return storeDestinations;
    }, [propDestinations, selectorDestinations, storeDestinations]);

    const availableDestinationIds = React.useMemo(
        () => resolvedDestinations.map((dest) => dest.stepConfigId),
        [resolvedDestinations],
    );

    const appStoreIntegration = useSelector((state: RootState) => selectAppStoreIntegration(state, appId));
    const stabilityIntegration = useSelector((state: RootState) => selectStabilityIntegration(state, appId));
    const availableVersions = selectorVersions;

    const availableMetricKeys = React.useMemo(() => getAvailableMetricKeys(SUPPORTED_STATISTIC_TYPES), []);

    const {
        pageView,
        tabularLayout,
        selectedMetricKeys,
        selectedVersions,
        selectedDestinationIds,
        updatePreferences,
        initializeWithDefaults,
        getStoredPreferences,
    } = useSummaryPreferences({
        orgId,
        appId,
        availableMetrics: availableMetricKeys,
        availableVersions,
        availableDestinationIds,
    });

    const preferences: OrgSummaryPreferencesSnapshot = React.useMemo(
        () => ({
            pageView,
            tabularLayout,
            selectedMetricKeys,
            selectedVersions,
            selectedDestinationIds,
        }),
        [pageView, tabularLayout, selectedMetricKeys, selectedVersions, selectedDestinationIds],
    );

    const [isFilterApplying, setIsFilterApplying] = React.useState(false);
    const [shouldDisplayFetchFailed, setShouldDisplayFetchFailed] = React.useState(false);
    const requestId = createSummaryRequestId(orgId, appId);
    const isLoadingSummaryView = useIsLoading(FetchableEntityType.OrgSummaryView, requestId);
    const fetchError = useError(FetchableEntityType.OrgSummaryView, requestId);

    React.useEffect(() => {
        if (fetchError) {
            setShouldDisplayFetchFailed(true);
            setIsFilterApplying(false);
        }
    }, [fetchError]);

    React.useEffect(() => {
        if (!isLoadingSummaryView && isFilterApplying) {
            setIsFilterApplying(false);
        }
    }, [isLoadingSummaryView, isFilterApplying]);

    const requestedItemsRef = React.useRef({
        versions: new Set<string>(),
        destinations: new Set<string>(),
        metrics: new Set<string>(),
    });

    const fetchSummaryView = React.useCallback(
        (params: FetchSummaryViewParams) => {
            params.versions.forEach((v) => requestedItemsRef.current.versions.add(v));
            params.destinationIds.forEach((d) => requestedItemsRef.current.destinations.add(d));
            params.properties.forEach((p) => requestedItemsRef.current.metrics.add(p));

            dispatch(
                tryFetchEntity(
                    FetchableEntityType.OrgSummaryView,
                    {
                        orgId,
                        destinationIds: params.destinationIds,
                        versions: params.versions,
                        properties: params.properties,
                    },
                    requestId,
                ),
            );
        },
        [dispatch, orgId, requestId],
    );

    const selectableVersions = React.useMemo(() => {
        const merged = new Set<string>();
        availableVersions.forEach((version) => merged.add(version));
        selectedVersions.forEach((version) => merged.add(version));
        return Array.from(merged).sort((a, b) => -compareVersions(a, b));
    }, [availableVersions, selectedVersions]);

    const summaryViewResponse = useSelector((state: RootState) => selectSummaryViewByRequestId(state, requestId));
    const metricOptions = React.useMemo(() => getAvailableMetricOptions(SUPPORTED_STATISTIC_TYPES), []);

    const transformedRows = React.useMemo(() => {
        if (!summaryViewResponse?.versionSummaryViewItems) return [];
        return transformToTableRows(summaryViewResponse.versionSummaryViewItems);
    }, [summaryViewResponse]);

    const filteredRows = React.useMemo(() => {
        return transformedRows.filter(
            (row) => selectedVersions.includes(row.version) && selectedDestinationIds.includes(row.destinationId),
        );
    }, [transformedRows, selectedVersions, selectedDestinationIds]);

    const metrics = React.useMemo(() => {
        return createSummaryViewMetrics(selectedMetricKeys, filteredRows);
    }, [selectedMetricKeys, filteredRows]);

    const destinationsById = React.useMemo(() => {
        const map: Record<string, Destination> = {};
        resolvedDestinations.forEach((dest) => {
            map[dest.stepConfigId] = dest;
        });
        return map;
    }, [resolvedDestinations]);

    const standardColumns = React.useMemo(
        () =>
            createStandardViewColumns({
                metrics,
                currentAppId: appId,
                destinationsById,
                appStoreIntegration,
                stabilityIntegration,
            }),
        [metrics, appId, destinationsById, appStoreIntegration, stabilityIntegration],
    );

    const compactViewConfig = React.useMemo(
        () =>
            createCompactViewConfig({
                releasesData: filteredRows,
                metrics,
                currentAppId: appId,
                destinationsById,
            }),
        [filteredRows, metrics, appId, destinationsById],
    );

    const handleFilterApply = React.useCallback(
        (newMetrics: string[], newVersions: string[]) => {
            const requested = requestedItemsRef.current;
            const unrequestedMetrics = newMetrics.filter((m) => !requested.metrics.has(m));
            const unrequestedVersions = newVersions.filter((v) => !requested.versions.has(v));

            const snapshot = updatePreferences({ metrics: newMetrics, versions: newVersions });
            const contexts = createFilterContexts(unrequestedMetrics, unrequestedVersions, snapshot);

            if (contexts.length > 0) {
                setShouldDisplayFetchFailed(false);
                setIsFilterApplying(true);
                contexts.forEach((context) => {
                    const request = buildFetchRequest(context);
                    fetchSummaryView(request);
                });
            }
        },
        [updatePreferences, fetchSummaryView],
    );

    const handleDestinationChange = React.useCallback(
        (ids: string[]) => {
            const requested = requestedItemsRef.current;
            const unrequestedDestinations = ids.filter((id) => !requested.destinations.has(id));

            const snapshot = updatePreferences({ destinations: ids });
            const contexts = createDestinationContext(unrequestedDestinations, snapshot);

            if (contexts.length > 0) {
                setShouldDisplayFetchFailed(false);
                setIsFilterApplying(true);
                contexts.forEach((context) => {
                    const request = buildFetchRequest(context);
                    fetchSummaryView(request);
                });
            }
        },
        [updatePreferences, fetchSummaryView],
    );

    const handlePageViewChange = React.useCallback(
        (value: PageViewType) => {
            updatePreferences({ pageView: value });
        },
        [updatePreferences],
    );

    const handleTabularLayoutChange = React.useCallback(
        (value: TabularLayoutType) => {
            updatePreferences({ tabularLayout: value });
        },
        [updatePreferences],
    );

    return {
        preferences,
        updatePreferences,
        initializeWithDefaults,
        getStoredPreferences,
        availableDestinations: resolvedDestinations,
        availableMetrics: metricOptions,
        selectableVersions,
        appStoreIntegration,
        stabilityIntegration,
        isFilterApplying,
        setIsFilterApplying,
        shouldDisplayFetchFailed,
        setShouldDisplayFetchFailed,
        fetchSummaryView,
        requestId,
        summaryViewResponse,
        isLoadingSummaryView,
        availableVersions,
        availableDestinationIds,
        selectedDestinationIds,
        handleFilterApply,
        handleDestinationChange,
        handlePageViewChange,
        handleTabularLayoutChange,
        isLoadingOrgDestinations,
        tableData: {
            standardColumns,
            compactConfig: compactViewConfig,
            transformedRows,
            filteredRows,
            metrics,
        },
    };
};
